import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent } from '@/components/ui/card';
import { ShoppingCart, Loader2, CheckCircle2, MapPin, User, MessageSquare } from 'lucide-react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostr } from '@nostrify/react';
import { useToast } from '@/hooks/useToast';
import {
  type Product,
  type Stall,
  type Order,
  generateId,
  formatPrice,
} from '@/lib/marketplace';

interface CartItem {
  product: Product;
  quantity: number;
}

interface CheckoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchantPubkey: string;
  stall: Stall;
  cart: CartItem[];
}

function calcTotal(cart: CartItem[], shippingZoneId: string, stall: Stall): number {
  const itemsTotal = cart.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const zone = stall.shipping.find(z => z.id === shippingZoneId);
  const baseShipping = zone?.cost ?? 0;
  const extraShipping = cart.reduce((sum, item) => {
    const extra = item.product.shipping?.find(s => s.id === shippingZoneId)?.cost ?? 0;
    return sum + extra * item.quantity;
  }, 0);
  return itemsTotal + baseShipping + extraShipping;
}

export function CheckoutDialog({
  open,
  onOpenChange,
  merchantPubkey,
  stall,
  cart,
}: CheckoutDialogProps) {
  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [message, setMessage] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [shippingId, setShippingId] = useState(stall.shipping[0]?.id ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const total = shippingId ? calcTotal(cart, shippingId, stall) : 0;
  const hasDigitalShipping = stall.shipping.some(z =>
    z.regions.some(r => r.toLowerCase().includes('digital') || r.toLowerCase().includes('no shipping'))
    || z.cost === 0
  );

  const handleSubmit = async () => {
    if (!user) {
      toast({ title: 'Login required', description: 'You must be logged in to place an order.', variant: 'destructive' });
      return;
    }
    if (!shippingId) {
      toast({ title: 'Select shipping', description: 'Please choose a shipping zone.', variant: 'destructive' });
      return;
    }
    if (cart.length === 0) {
      toast({ title: 'Empty cart', variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);
    try {
      const order: Order = {
        id: generateId(),
        type: 0,
        name: name.trim() || undefined,
        address: address.trim() || undefined,
        message: message.trim() || undefined,
        contact: {
          nostr: user.pubkey,
          phone: phone.trim() || undefined,
          email: email.trim() || undefined,
        },
        items: cart.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity,
        })),
        shipping_id: shippingId,
      };

      // NIP-04 encrypt the order JSON to the merchant
      const encrypted = await user.signer.nip04?.encrypt(merchantPubkey, JSON.stringify(order));
      if (!encrypted) {
        throw new Error('Your signer does not support NIP-04 encryption. Please use a compatible signer.');
      }

      const dmEvent = await user.signer.signEvent({
        kind: 4,
        content: encrypted,
        tags: [['p', merchantPubkey]],
        created_at: Math.floor(Date.now() / 1000),
      });

      await nostr.event(dmEvent, { signal: AbortSignal.timeout(8000) });

      setSubmitted(true);
      toast({ title: 'Order sent!', description: 'Your order has been sent to the merchant via encrypted DM.' });
    } catch (err) {
      toast({
        title: 'Order failed',
        description: (err as Error).message,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-primary" />
            Checkout — {stall.name}
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="text-center py-8 space-y-3">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
            <h3 className="text-lg font-semibold">Order Sent!</h3>
            <p className="text-sm text-muted-foreground">
              Your order has been securely sent to the merchant via encrypted NIP-04 direct message.
              They will reply with payment instructions.
            </p>
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Order summary */}
            <div className="space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Order Summary
              </h4>
              {cart.map(item => (
                <div key={item.product.id} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-[10px] px-1.5 h-4">
                      ×{item.quantity}
                    </Badge>
                    <span className="font-medium">{item.product.name}</span>
                  </div>
                  <span className="text-muted-foreground">
                    {formatPrice(item.product.price * item.quantity, stall.currency)}
                  </span>
                </div>
              ))}
            </div>

            <Separator />

            {/* Shipping zone */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                Shipping Zone *
              </Label>
              <Select value={shippingId} onValueChange={setShippingId}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select shipping zone" />
                </SelectTrigger>
                <SelectContent>
                  {stall.shipping.map(zone => (
                    <SelectItem key={zone.id} value={zone.id} className="text-sm">
                      {zone.name || `Zone ${zone.id.slice(0, 8)}`}
                      {' '}— {formatPrice(zone.cost, stall.currency)} base
                      {zone.regions.length > 0 && ` (${zone.regions.join(', ')})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Total */}
            {shippingId && (
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="py-3 px-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Total</span>
                    <span className="text-lg font-bold text-primary">
                      {formatPrice(total, stall.currency)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            <Separator />

            {/* Shipping address (optional for digital goods) */}
            {!hasDigitalShipping && (
              <div className="space-y-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  Shipping Address
                </h4>
                <div className="space-y-1.5">
                  <Label className="text-xs">Full Name</Label>
                  <Input
                    placeholder="Your full name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    className="h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Shipping Address</Label>
                  <Textarea
                    placeholder="Street, City, State/Province, Country, Postal Code"
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    rows={3}
                    className="text-sm resize-none"
                  />
                </div>
              </div>
            )}

            {/* Contact */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <User className="h-3 w-3" />
                Contact (optional)
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Phone</Label>
                  <Input
                    placeholder="+1 555 000 0000"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Email</Label>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            </div>

            {/* Message */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium flex items-center gap-1">
                <MessageSquare className="h-3.5 w-3.5" />
                Message to Merchant (optional)
              </Label>
              <Textarea
                placeholder="Any special requests or notes…"
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={2}
                className="text-sm resize-none"
              />
            </div>

            {!user && (
              <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-400 px-3 py-2 rounded-lg border border-amber-200 dark:border-amber-800">
                You must be logged in to place an order.
              </p>
            )}

            <p className="text-[11px] text-muted-foreground">
              Your order will be sent as an encrypted NIP-04 direct message to the merchant.
              They will reply with payment details via DM.
            </p>

            <div className="flex gap-3 pt-1">
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !user || !shippingId || cart.length === 0}
                size="sm"
                className="flex-1 gap-2"
              >
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
                Place Order
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
